import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { adminPhotoData, categoryLabels, categoryIcons } from '@/mocks/adminPhotos';
import type { PhotoCategory, AdminPhoto, AdminAlbum } from '@/mocks/adminPhotos';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { useUpgradeModal } from '@/context/UpgradeModalContext';
import StorageBar from './components/StorageBar';
import UploadZone from './components/UploadZone';
import PhotoGrid from './components/PhotoGrid';
import AlbumManager from './components/AlbumManager';
import PhotoEditModal from './components/PhotoEditModal';

const allCategories: PhotoCategory[] = ['cabina', 'eventos', 'locutores', 'comunidad', 'promociones'];

const sharedSwal = Swal.mixin({
  customClass: {
    popup: 'rounded-2xl',
    title: 'font-bold',
    confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
    htmlContainer: 'text-sm',
  },
  buttonsStyling: false,
});

export default function AdminPhotos() {
  const { isFree, storageUsedGB, storageTotalGB, storagePercentage, planId } = usePlanPermissions();
  const { openUpgradeModal } = useUpgradeModal();

  const [photos, setPhotos] = useState<AdminPhoto[]>([]);
  const [albums, setAlbums] = useState<AdminAlbum[]>([]);

  const [filterCategory, setFilterCategory] = useState<PhotoCategory | 'todas'>('todas');
  const [filterAlbum, setFilterAlbum] = useState<number | 'todos'>('todos');

  const [albumModalOpen, setAlbumModalOpen] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState<AdminAlbum | null>(null);

  const [editingPhoto, setEditingPhoto] = useState<AdminPhoto | null>(null);
  const [photoEditModalOpen, setPhotoEditModalOpen] = useState(false);

  const [lightboxPhoto, setLightboxPhoto] = useState<AdminPhoto | null>(null);
  const [sidebarAlbums, setSidebarAlbums] = useState(true);
  const [pageState, setPageState] = useState<'loading' | 'error' | 'ready'>('loading');

  // Simulate loading
  useEffect(() => {
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.07;
      if (shouldFail) {
        setPageState('error');
      } else {
        setPhotos(adminPhotoData.photos);
        setAlbums(adminPhotoData.albums);
        setPageState('ready');
      }
    }, 900 + Math.random() * 800);
    return () => clearTimeout(timer);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    const timer = setTimeout(() => {
      const shouldFail = Math.random() < 0.05;
      if (shouldFail) {
        setPageState('error');
      } else {
        setPhotos(adminPhotoData.photos);
        setAlbums(adminPhotoData.albums);
        setPageState('ready');
      }
    }, 600 + Math.random() * 600);
    return () => clearTimeout(timer);
  }, []);

  /* ─── Filtrado ─── */
  const filteredPhotos = useMemo(() => {
    return photos.filter((p) => {
      const matchCat = filterCategory === 'todas' || p.category === filterCategory;
      const matchAlbum = filterAlbum === 'todos' || p.albumId === filterAlbum;
      return matchCat && matchAlbum;
    });
  }, [photos, filterCategory, filterAlbum]);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const featured = photos.filter((p) => p.isFeatured).length;
    const thisMonth = photos.filter((p) => {
      const now = new Date();
      const d = new Date(p.uploadedAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
    return { featured, thisMonth, total: photos.length, albums: albums.length };
  }, [photos, albums]);

  /* ─── Upload handler ─── */
  const handleUpload = (files: File[]) => {
    if (isFree) {
      sharedSwal.fire({
        title: 'Plan Free limitado',
        html: '<p class="text-sm text-foreground-500 leading-relaxed">Tu plan Free no incluye fotos. Actualiza a Pro.</p>',
        icon: 'warning',
        confirmButtonText: 'Ver planes',
        confirmButtonColor: '#f59e0b',
        showCancelButton: true,
        cancelButtonText: 'Cerrar',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) {
          openUpgradeModal();
        }
      });
      return;
    }

    if (storagePercentage >= 95) {
      sharedSwal.fire({
        title: 'Almacenamiento lleno',
        html: `<p class="text-sm text-foreground-500 leading-relaxed">Tu almacenamiento está al <strong>${storagePercentage}%</strong> (${storageUsedGB.toFixed(1)} GB de ${storageTotalGB} GB). Eliminá fotos viejas o mejorá tu plan para seguir subiendo.</p>`,
        icon: 'error',
        confirmButtonText: 'Administrar almacenamiento',
        confirmButtonColor: '#ef4444',
        showCancelButton: true,
        cancelButtonText: 'Cerrar',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Revisá tus fotos y eliminá las que no necesites.',
            icon: 'info',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
      return;
    }

    const newPhotos: AdminPhoto[] = files.map((f, i) => ({
      id: Math.max(...photos.map((p) => p.id), 0) + i + 1,
      title: f.name.replace(/\.[^.]+$/, ''),
      imageUrl: URL.createObjectURL(f),
      albumId: filterAlbum !== 'todos' ? filterAlbum : albums[0]?.id || 1,
      category: 'cabina',
      isFeatured: false,
      fileSize: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
      dimensions: '-- x --',
      uploadedAt: new Date().toISOString(),
    }));
    setPhotos((prev) => [...newPhotos, ...prev]);

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: files.length === 1 ? 'Foto subida exitosamente.' : `${files.length} fotos subidas exitosamente.`,
      icon: 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  /* ─── Delete photo ─── */
  const handleDeletePhoto = (photo: AdminPhoto) => {
    sharedSwal
      .fire({
        title: '¿Eliminar foto?',
        html: `<p class="text-sm text-foreground-500">Se eliminará permanentemente <strong>&ldquo;${escapeHtml(photo.title)}&rdquo;</strong>. Esta acción no se puede deshacer.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setPhotos((prev) => prev.filter((p) => p.id !== photo.id));

          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Foto eliminada.',
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
  };

  /* ─── Toggle featured ─── */
  const handleToggleFeatured = (photo: AdminPhoto) => {
    const newFeatured = !photo.isFeatured;
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === photo.id ? { ...p, isFeatured: newFeatured } : p
      )
    );

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: newFeatured ? 'Marcada como destacada.' : 'Quitada de destacadas.',
      icon: 'success',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  /* ─── Edit photo ─── */
  const handleEditPhoto = (photo: AdminPhoto) => {
    setEditingPhoto(photo);
    setPhotoEditModalOpen(true);
  };

  const handleSavePhotoEdit = (data: { id: number; title: string; category: PhotoCategory; albumId: number }) => {
    setPhotos((prev) =>
      prev.map((p) =>
        p.id === data.id
          ? { ...p, title: data.title, category: data.category, albumId: data.albumId }
          : p
      )
    );
    setPhotoEditModalOpen(false);
    setEditingPhoto(null);

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: 'Foto actualizada.',
      icon: 'success',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  /* ─── Share photo ─── */
  const handleSharePhoto = (photo: AdminPhoto) => {
    const shareUrl = `${window.location.origin}/foto/${photo.id}`;

    sharedSwal.fire({
      title: 'Compartir foto',
      html: `
        <div class="space-y-3 text-left">
          <div class="bg-background-100 rounded-xl p-3 flex items-center gap-3">
            <div class="w-14 h-14 rounded-lg overflow-hidden bg-background-200 shrink-0">
              <img src="${photo.imageUrl}" alt="${escapeHtml(photo.title)}" class="w-full h-full object-cover" />
            </div>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-foreground-900 line-clamp-1">${escapeHtml(photo.title)}</p>
              <p class="text-[10px] text-foreground-400 mt-0.5">${photo.fileSize} &bull; ${photo.dimensions}</p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <input
              type="text"
              value="${shareUrl}"
              readonly
              class="flex-1 px-3 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-700 text-xs focus:outline-none"
              id="swal-share-url"
            />
            <button
              id="swal-copy-btn"
              class="px-3 py-2 rounded-xl bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
            >
              <i class="ri-file-copy-line mr-1"></i> Copiar
            </button>
          </div>
          <div class="flex items-center gap-2">
            <button id="swal-share-fb" class="flex-1 px-3 py-2 rounded-xl bg-[#1877F2] text-white text-xs font-semibold hover:opacity-90 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-facebook-fill mr-1"></i> Facebook
            </button>
            <button id="swal-share-tw" class="flex-1 px-3 py-2 rounded-xl bg-black text-white text-xs font-semibold hover:opacity-80 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-twitter-x-fill mr-1"></i> X
            </button>
            <button id="swal-share-wa" class="flex-1 px-3 py-2 rounded-xl bg-[#25D366] text-white text-xs font-semibold hover:opacity-90 cursor-pointer transition-opacity whitespace-nowrap text-center">
              <i class="ri-whatsapp-fill mr-1"></i> WhatsApp
            </button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      customClass: { popup: 'rounded-2xl max-w-md' },
      didOpen: () => {
        const copyBtn = document.getElementById('swal-copy-btn');
        const urlInput = document.getElementById('swal-share-url') as HTMLInputElement;
        if (copyBtn && urlInput) {
          copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(shareUrl).then(() => {
              copyBtn.innerHTML = '<i class="ri-check-line mr-1"></i> Copiado';
              copyBtn.classList.add('bg-emerald-500');
              setTimeout(() => {
                copyBtn.innerHTML = '<i class="ri-file-copy-line mr-1"></i> Copiar';
                copyBtn.classList.remove('bg-emerald-500');
              }, 2000);
            });
          });
        }
        const fbBtn = document.getElementById('swal-share-fb');
        const twBtn = document.getElementById('swal-share-tw');
        const waBtn = document.getElementById('swal-share-wa');
        const encodedUrl = encodeURIComponent(shareUrl);
        const encodedTitle = encodeURIComponent(photo.title);
        if (fbBtn) fbBtn.addEventListener('click', () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank'));
        if (twBtn) twBtn.addEventListener('click', () => window.open(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`, '_blank'));
        if (waBtn) waBtn.addEventListener('click', () => window.open(`https://wa.me/?text=${encodedTitle}%20${encodedUrl}`, '_blank'));
      },
    });
  };

  /* ─── Set photo as album cover ─── */
  const handleSetCover = (photo: AdminPhoto) => {
    sharedSwal
      .fire({
        title: 'Setear como portada',
        html: `
          <p class="text-sm text-foreground-500 leading-relaxed mb-3">
            Seleccioná el álbum para usar <strong>&ldquo;${escapeHtml(photo.title)}&rdquo;</strong> como portada:
          </p>
          <select id="swal-cover-album" class="swal2-select w-full px-3 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none">
            ${albums.map((a) => `<option value="${a.id}">${escapeHtml(a.name)}</option>`).join('')}
          </select>
        `,
        showCancelButton: true,
        confirmButtonText: 'Aplicar portada',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#9ca3af',
        didOpen: () => {
          // pre-select the photo's current album
          const sel = document.getElementById('swal-cover-album') as HTMLSelectElement;
          if (sel) sel.value = String(photo.albumId);
        },
      })
      .then((result) => {
        if (result.isConfirmed) {
          const albumSelect = document.getElementById('swal-cover-album') as HTMLSelectElement;
          const selectedAlbumId = Number(albumSelect?.value || photo.albumId);

          setAlbums((prev) =>
            prev.map((a) =>
              a.id === selectedAlbumId ? { ...a, coverUrl: photo.imageUrl } : a
            )
          );

          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Portada del álbum actualizada.',
            icon: 'success',
            showConfirmButton: false,
            timer: 2500,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
  };

  /* ─── Lightbox ─── */
  const handlePhotoClick = (photo: AdminPhoto) => {
    setLightboxPhoto(photo);
  };

  /* ─── Album handlers ─── */
  const handleCreateAlbum = () => {
    if (isFree) {
      sharedSwal.fire({
        title: 'Plan Free limitado',
        html: '<p class="text-sm text-foreground-500 leading-relaxed">Tu plan Free no incluye fotos. Actualiza a Pro para crear álbumes.</p>',
        icon: 'warning',
        confirmButtonText: 'Ver planes',
        confirmButtonColor: '#f59e0b',
        showCancelButton: true,
        cancelButtonText: 'Cerrar',
        cancelButtonColor: '#9ca3af',
      }).then((result) => {
        if (result.isConfirmed) {
          openUpgradeModal();
        }
      });
      return;
    }
    setEditingAlbum(null);
    setAlbumModalOpen(true);
  };

  const handleEditAlbum = (album: AdminAlbum) => {
    setEditingAlbum(album);
    setAlbumModalOpen(true);
  };

  const handleSaveAlbum = (data: { name: string; category: PhotoCategory }) => {
    if (editingAlbum) {
      setAlbums((prev) =>
        prev.map((a) =>
          a.id === editingAlbum.id ? { ...a, name: data.name, category: data.category } : a
        )
      );
    } else {
      const newAlbum: AdminAlbum = {
        id: Math.max(...albums.map((a) => a.id), 0) + 1,
        name: data.name,
        coverUrl:
          'https://readdy.ai/api/search-image?query=Empty%20photo%20album%20cover%20design%2C%20clean%20minimalist%20aesthetic%2C%20soft%20gradient%20background%2C%20subtle%20texture%2C%20elegant%20placeholder%20for%20photo%20collection&width=600&height=400&seq=album-new-placeholder&orientation=landscape',
        photoCount: 0,
        category: data.category,
        createdAt: new Date().toISOString(),
      };
      setAlbums((prev) => [...prev, newAlbum]);
    }
    setAlbumModalOpen(false);
    setEditingAlbum(null);

    Swal.fire({
      toast: true,
      position: 'top-end',
      title: editingAlbum ? 'Álbum actualizado.' : 'Álbum creado exitosamente.',
      icon: 'success',
      showConfirmButton: false,
      timer: 2500,
      timerProgressBar: true,
      customClass: { popup: 'rounded-xl' },
    });
  };

  const handleDeleteAlbum = (album: AdminAlbum) => {
    sharedSwal
      .fire({
        title: '¿Eliminar álbum?',
        html: `<p class="text-sm text-foreground-500">Se eliminará el álbum <strong>&ldquo;${escapeHtml(album.name)}&rdquo;</strong> y todas sus fotos. Esta acción no se puede deshacer.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#9ca3af',
      })
      .then((result) => {
        if (result.isConfirmed) {
          setAlbums((prev) => prev.filter((a) => a.id !== album.id));
          setPhotos((prev) => prev.filter((p) => p.albumId !== album.id));
          if (filterAlbum === album.id) setFilterAlbum('todos');

          Swal.fire({
            toast: true,
            position: 'top-end',
            title: 'Álbum eliminado.',
            icon: 'success',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            customClass: { popup: 'rounded-xl' },
          });
        }
      });
  };

  /* ─── Storage derived ─── */
  const storageDisplay = useMemo(() => {
    const used = `${storageUsedGB.toFixed(1)} GB`;
    const total = `${storageTotalGB} GB`;
    return {
      used,
      total,
      percentage: storagePercentage,
      usedGB: storageUsedGB,
      totalGB: storageTotalGB,
      photoCount: photos.length,
    };
  }, [storageUsedGB, storageTotalGB, storagePercentage, photos.length]);

  // ---- Loading Skeleton ----
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-background-200 animate-pulse"></div>
          </div>
        </header>
        <div className="flex">
          <main className="flex-1 min-w-0">
            <div className="hidden lg:flex items-center h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
              <div className="w-40 h-4 rounded-md bg-background-200 animate-pulse"></div>
            </div>
            <div className="p-4 md:p-6 lg:p-8 space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-background-50 rounded-2xl border border-background-200 p-4">
                    <div className="w-16 h-3 rounded bg-background-200 animate-pulse mb-2"></div>
                    <div className="w-10 h-7 rounded bg-background-200 animate-pulse mb-1"></div>
                    <div className="w-20 h-2 rounded bg-background-200 animate-pulse"></div>
                  </div>
                ))}
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-8">
                <div className="w-40 h-4 rounded bg-background-200 animate-pulse mx-auto mb-3"></div>
                <div className="w-60 h-3 rounded bg-background-200 animate-pulse mx-auto"></div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="aspect-[4/3] rounded-xl bg-background-200 animate-pulse"></div>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="aspect-square rounded-xl bg-background-200 animate-pulse"></div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ---- Error State ----
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-100">
        <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
          <div className="flex items-center px-4 h-14">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-image-line text-background-50 text-xs"></i>
            </div>
          </div>
        </header>
        <div className="flex">
          <main className="flex-1 min-w-0 flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-red-100 flex items-center justify-center mb-4">
                <i className="ri-error-warning-line text-red-500 text-2xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-950 mb-1">
                Error cargando galería
              </h2>
              <p className="text-sm text-foreground-500 mb-5 leading-relaxed">
                No se pudieron cargar las fotos y álbumes. Verificá tu conexión e intentá de nuevo.
              </p>
              <button
                onClick={handleRetry}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-refresh-line text-base"></i>
                Reintentar
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ---- Ready State ----
  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200 safe-top">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-accent-500 flex items-center justify-center">
              <i className="ri-image-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Galería de Fotos</h1>
              <p className="text-foreground-400 text-[9px]">{photos.length} fotos &bull; {albums.length} álbumes</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <Link
              to="/admin/posts"
              className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
            >
              Publicaciones
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
            >
              <i className="ri-arrow-left-line text-xs"></i>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Main content */}
        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Galería de Fotos</h2>
                <p className="text-foreground-400 text-xs">
                  {photos.length} fotos en {albums.length} álbumes
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <Link
                  to="/admin/posts"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-500 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-article-line text-xs mr-1"></i>
                  Publicaciones
                </Link>
                <Link
                  to="/admin/dashboard"
                  className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-foreground-400 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <i className="ri-dashboard-line text-xs mr-1"></i>
                  Dashboard
                </Link>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleCreateAlbum}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-secondary-500 text-background-50 text-xs font-semibold hover:bg-secondary-600 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className="ri-folder-add-line text-sm"></i>
                Crear álbum
              </button>
              <Link
                to="/"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-external-link-line text-xs"></i>
                Ver sitio
              </Link>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5">
            {/* Stats mini cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Fotos</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.total}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">{stats.thisMonth} este mes</p>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Álbumes</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.albums}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">5 categorías</p>
              </div>
              <div className="bg-background-50 rounded-2xl border border-background-200 p-4">
                <p className="text-[10px] font-semibold text-foreground-400 uppercase tracking-wide">Destacadas</p>
                <p className="text-2xl font-bold text-foreground-950 mt-1">{stats.featured}</p>
                <div className="flex items-center gap-0.5 mt-0.5">
                  <i className="ri-star-fill text-amber-500 text-[10px]"></i>
                  <span className="text-[10px] text-foreground-400">Fotos estrella</span>
                </div>
              </div>
              <StorageBar
                usedGB={storageDisplay.usedGB}
                totalGB={storageDisplay.totalGB}
                percentage={storageDisplay.percentage}
                used={storageDisplay.used}
                total={storageDisplay.total}
                photoCount={storageDisplay.photoCount}
                planLabel={planId === 'free' ? 'Free' : planId === 'pro' ? 'Pro' : 'Premium'}
              />
            </div>

            {/* Upload Zone */}
            <UploadZone onUpload={handleUpload} isFreePlan={isFree} />

            {/* Álbumes */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground-900">Álbumes</h3>
                <button
                  onClick={() => setSidebarAlbums(!sidebarAlbums)}
                  className="text-[11px] font-semibold text-foreground-400 hover:text-foreground-600 cursor-pointer whitespace-nowrap"
                >
                  {sidebarAlbums ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>

              {sidebarAlbums && albums.length === 0 ? (
                <div className="text-center py-8 bg-background-50 rounded-2xl border border-background-200 mb-4">
                  <div className="w-12 h-12 mx-auto rounded-xl bg-background-200 flex items-center justify-center mb-3">
                    <i className="ri-folder-open-line text-foreground-400 text-lg"></i>
                  </div>
                  <p className="text-sm font-semibold text-foreground-600">Sin álbumes</p>
                  <p className="text-xs text-foreground-400 mt-1 mb-4">
                    Creá tu primer álbum para organizar tus fotos.
                  </p>
                  <button
                    onClick={handleCreateAlbum}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-secondary-500 text-background-50 text-xs font-semibold hover:bg-secondary-600 cursor-pointer transition-colors whitespace-nowrap"
                  >
                    <i className="ri-folder-add-line text-sm"></i>
                    Crear álbum
                  </button>
                </div>
              ) : (
                sidebarAlbums && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-4">
                    {albums.map((album) => {
                      const albumPhotos = photos.filter((p) => p.albumId === album.id);
                      const actualCount = albumPhotos.length;
                      return (
                        <button
                          key={album.id}
                          onClick={() => setFilterAlbum(filterAlbum === album.id ? 'todos' : album.id)}
                          className={`relative rounded-xl overflow-hidden bg-background-200 cursor-pointer transition-all group border-2 ${
                            filterAlbum === album.id ? 'border-accent-500' : 'border-transparent'
                          }`}
                        >
                          <div className="aspect-[4/3] w-full">
                            <img
                              src={album.coverUrl}
                              alt={album.name}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex flex-col justify-end p-3">
                            <p className="text-background-50 text-xs font-bold leading-tight">
                              {album.name}
                            </p>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-background-50/70 text-[10px]">
                                {actualCount} fotos
                              </span>
                              <span className="px-1.5 py-0.5 rounded-md bg-background-50/20 text-background-50/80 text-[9px] font-semibold">
                                {categoryLabels[album.category]}
                              </span>
                            </div>
                          </div>

                          {/* Edit/Delete on hover */}
                          <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditAlbum(album);
                              }}
                              className="w-7 h-7 rounded-lg bg-background-50/30 flex items-center justify-center cursor-pointer hover:bg-background-50/50 transition-colors"
                            >
                              <i className="ri-pencil-line text-background-50 text-xs"></i>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteAlbum(album);
                              }}
                              className="w-7 h-7 rounded-lg bg-background-50/30 flex items-center justify-center cursor-pointer hover:bg-red-500/80 transition-colors"
                            >
                              <i className="ri-delete-bin-line text-background-50 text-xs"></i>
                            </button>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )
              )}
            </div>

            {/* Photo Grid */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-foreground-900">
                  Fotos
                  <span className="text-foreground-400 font-normal text-xs ml-2">
                    {filteredPhotos.length} resultados
                  </span>
                </h3>
              </div>
              <PhotoGrid
                photos={filteredPhotos}
                categories={allCategories}
                filterCategory={filterCategory}
                onChangeCategory={setFilterCategory}
                filterAlbum={filterAlbum}
                onChangeAlbum={setFilterAlbum}
                albums={albums.map((a) => ({ id: a.id, name: a.name }))}
                onDelete={handleDeletePhoto}
                onToggleFeatured={handleToggleFeatured}
                onPhotoClick={handlePhotoClick}
                onEdit={handleEditPhoto}
                onShare={handleSharePhoto}
                onSetCover={handleSetCover}
              />
            </div>
          </div>
        </main>
      </div>



      {/* Album Manager Modal */}
      <AlbumManager
        open={albumModalOpen}
        album={editingAlbum}
        categories={allCategories}
        onClose={() => {
          setAlbumModalOpen(false);
          setEditingAlbum(null);
        }}
        onSave={handleSaveAlbum}
      />

      {/* Photo Edit Modal */}
      <PhotoEditModal
        open={photoEditModalOpen}
        photo={editingPhoto}
        categories={allCategories}
        albums={albums.map((a) => ({ id: a.id, name: a.name }))}
        onClose={() => {
          setPhotoEditModalOpen(false);
          setEditingPhoto(null);
        }}
        onSave={handleSavePhotoEdit}
      />

      {/* Lightbox */}
      {lightboxPhoto && (
        <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)}>
          <button
            onClick={() => setLightboxPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-background-50/10 flex items-center justify-center cursor-pointer hover:bg-background-50/20 transition-colors z-10"
          >
            <i className="ri-close-line text-background-50 text-xl"></i>
          </button>
          <div className="max-w-4xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxPhoto.imageUrl} alt={lightboxPhoto.title} className="w-full h-full object-contain rounded-xl" />
          </div>
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-background-50/90 backdrop-blur-md rounded-xl px-4 py-2.5">
            <p className="text-foreground-900 text-sm font-semibold text-center">{lightboxPhoto.title}</p>
            <p className="text-foreground-400 text-[11px] text-center mt-0.5">
              {lightboxPhoto.dimensions} &bull; {lightboxPhoto.fileSize}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}